import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Grid, IconButton, Paper, Switch, Tab, Tabs, TextField, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";
//import { Editor } from "@tinymce/tinymce-react";
import JoditEditor from 'jodit-react';
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";

const TABS = [
  { value: "general", label: "General" },
  { value: "categories", label: "Categories" },
  { value: "terms", label: "Terms & Conditions" },
  // { value: "notifications", label: "Notifications" }, // hidden — coming soon
  // { value: "integrations", label: "Integrations" },   // hidden — coming soon
];

const getValidTab = (tab) => {
  if (TABS.some((t) => t.value === tab)) return tab;
  return "general";
};

const TabPanel = ({ activeTab, tab, children }) => {
  if (activeTab !== tab) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
};

// Shorten a file name for display while keeping its extension visible,
// e.g. "Participant Liability Release Form.pdf" -> "Participant Liability R….pdf"
const truncateFileName = (fileName, maxLength = 27) => {
  if (!fileName || fileName.length <= maxLength) return fileName;
  const dot = fileName.lastIndexOf(".");
  if (dot === -1) return `${fileName.slice(0, maxLength)}…`;
  const ext = fileName.slice(dot); // includes the dot, e.g. ".pdf"
  const base = fileName.slice(0, dot);
  const keep = Math.max(1, maxLength - ext.length - 1);
  return `${base.slice(0, keep)}…${ext}`;
};

const Settings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const editor = useRef(null);

  const config = useMemo(
    () => ({
      readonly: false, // Set to true to make the editor read-only
    placeholder: 'Start typing your content here...',
    height: 400,
    toolbarButtonSize: 'middle',
    theme: 'default', // Options: 'default' or 'dark'
    enableDragAndDropFileToEditor: true,
    
    // Customize which toolbar buttons appear
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'font', 'fontsize', 'paragraph', '|',
      'image', 'table', 'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'fullsize', 'source'
    ],
    
    // Configure file uploads
    uploader: {
      insertImageAsBase64URI: true // Quickly inserts images directly as base64
    },
    
    // Hide the bottom status bar statistics if desired
    showXPathInStatusbar: false,
    showCharsCounter: true,
    showWordsCounter: true,
    }),
    []
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(() => getValidTab(searchParams.get("tab")), [searchParams]);

  const [agreementsEnabled, setAgreementsEnabled] = useState(true);
  const [agreements, setAgreements] = useState([]);
  const [agreementsLoading, setAgreementsLoading] = useState(false);
  const [agreementsSaving, setAgreementsSaving] = useState(false);
  const [agreementsLoadedOnce, setAgreementsLoadedOnce] = useState(false);
  const fileInputRefs = useRef({});

  const fetchAgreements = useCallback(async (silent = false) => {
    setAgreementsLoading(true);
    try {
      const resp = await axiosPrivate.get("/api/agreements");
      setAgreements(resp.data || []);
    } catch (e) {
      if (!silent) enqueueSnackbar("Failed to load agreements", { variant: "error" });
    } finally {
      setAgreementsLoading(false);
    }
  }, [axiosPrivate, enqueueSnackbar]);

  useEffect(() => {
    if (activeTab !== "general" || agreementsLoadedOnce) return;
    setAgreementsLoadedOnce(true);
    fetchAgreements(true);
  }, [activeTab, agreementsLoadedOnce, fetchAgreements]);

  const handleAgreementChange = (id, field, value) => {
    setAgreements((prev) =>
      prev.map((a) => (a.id === id ? { ...a, [field]: value } : a))
    );
  };

  const handleSaveAgreements = async () => {
    setAgreementsSaving(true);
    try {
      await Promise.all(
        agreements.map((a) =>
          axiosPrivate.put(`/api/agreements/${a.id}`, {
            name: a.name,
            enabled: a.enabled,
            registrationFieldLabel: a.registrationFieldLabel,
            instructions: a.instructions,
            linkText: a.linkText,
          })
        )
      );
      enqueueSnackbar("Agreements saved", { variant: "success" });
      await fetchAgreements(true);
    } catch (e) {
      enqueueSnackbar("Failed to save agreements", { variant: "error" });
    } finally {
      setAgreementsSaving(false);
    }
  };

  const handleReplacePdf = async (agreementId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("pdf", file);
    try {
      await axiosPrivate.post(`/api/agreements/${agreementId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchAgreements(true);
      enqueueSnackbar("PDF uploaded", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Failed to upload PDF", { variant: "error" });
    }
  };

  // --- Supplemental Insurance ---
  const [suppInsuranceEnabled, setSuppInsuranceEnabled] = useState(true);
  const [suppInsurancePrompt, setSuppInsurancePrompt] = useState("Please select an option");
  const [allowSelfProvided, setAllowSelfProvided] = useState(true);
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [newInsuranceName, setNewInsuranceName] = useState("");
  const [newInsurancePrice, setNewInsurancePrice] = useState("");
  const [addingInsurance, setAddingInsurance] = useState(false);
  const [insuranceLoadedOnce, setInsuranceLoadedOnce] = useState(false);
  const [insuranceSaving, setInsuranceSaving] = useState(false);

  const toInsuranceOption = (o) => ({
    id: o.id,
    name: o.label,
    price: o.price,
    sortOrder: o.sortOrder,
    editing: false,
    editName: "",
    editPrice: "",
  });

  const fetchInsurance = useCallback(async () => {
    try {
      const [settingsResp, optionsResp] = await Promise.all([
        axiosPrivate.get("/api/insurance/settings"),
        axiosPrivate.get("/api/insurance"),
      ]);
      const s = settingsResp.data;
      setSuppInsuranceEnabled(s.enabled);
      setSuppInsurancePrompt(s.prompt);
      setAllowSelfProvided(s.allowSelfProvided);
      setInsuranceOptions((optionsResp.data || []).map(toInsuranceOption));
    } catch (e) {
      enqueueSnackbar("Failed to load insurance settings", { variant: "error" });
    }
  }, [axiosPrivate, enqueueSnackbar]);

  useEffect(() => {
    if (activeTab !== "general" || insuranceLoadedOnce) return;
    setInsuranceLoadedOnce(true);
    fetchInsurance();
  }, [activeTab, insuranceLoadedOnce, fetchInsurance]);

  const handleSaveAllSettings = async () => {
    setInsuranceSaving(true);
    setBgCheckSaving(true);
    setImmunizationsSaving(true);
    try {
      await Promise.all([
        axiosPrivate.put("/api/insurance/settings", {
          enabled: suppInsuranceEnabled,
          prompt: suppInsurancePrompt,
          allowSelfProvided,
        }),
        axiosPrivate.put("/api/background-check/settings", {
          enabled: bgCheckEnabled,
          prompt: bgCheckPrompt,
        }),
        axiosPrivate.put("/api/immunizations/settings", {
          enabled: immunizationsEnabled,
          prompt: immunizationsPrompt,
        }),
      ]);
      enqueueSnackbar("Settings saved", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Failed to save settings", { variant: "error" });
    } finally {
      setInsuranceSaving(false);
      setBgCheckSaving(false);
      setImmunizationsSaving(false);
    }
  };

  const startEditInsurance = (id) => setInsuranceOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: true, editName: o.name, editPrice: o.price } : o));
  const cancelEditInsurance = (id) => setInsuranceOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: false } : o));

  const saveEditInsurance = async (id) => {
    const opt = insuranceOptions.find((o) => o.id === id);
    if (!opt) return;
    setInsuranceOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: false, name: o.editName, price: o.editPrice } : o));
    try {
      await axiosPrivate.put(`/api/insurance/options/${id}`, { label: opt.editName, price: opt.editPrice });
    } catch (e) {
      enqueueSnackbar("Failed to save option", { variant: "error" });
      await fetchInsurance();
    }
  };

  const deleteInsuranceOption = async (id) => {
    setInsuranceOptions((prev) => prev.filter((o) => o.id !== id));
    try {
      await axiosPrivate.delete(`/api/insurance/options/${id}`);
    } catch (e) {
      enqueueSnackbar("Failed to delete option", { variant: "error" });
      await fetchInsurance();
    }
  };

  const addInsuranceOption = async () => {
    if (!newInsuranceName.trim()) return;
    const name = newInsuranceName.trim();
    const price = newInsurancePrice.trim();
    setNewInsuranceName("");
    setNewInsurancePrice("");
    setAddingInsurance(false);
    try {
      const resp = await axiosPrivate.post("/api/insurance/options", { label: name, price });
      setInsuranceOptions((prev) => [...prev, toInsuranceOption(resp.data)]);
    } catch (e) {
      enqueueSnackbar("Failed to add option", { variant: "error" });
    }
  };

  // --- Background Check ---
  const [bgCheckEnabled, setBgCheckEnabled] = useState(true);
  const [bgCheckPrompt, setBgCheckPrompt] = useState("Please select an option");
  const [bgCheckOptions, setBgCheckOptions] = useState([]);
  const [newBgCheckName, setNewBgCheckName] = useState("");
  const [addingBgCheck, setAddingBgCheck] = useState(false);
  const [bgCheckLoadedOnce, setBgCheckLoadedOnce] = useState(false);
  const [bgCheckSaving, setBgCheckSaving] = useState(false);

  const toBgCheckOption = (o) => ({
    id: o.id,
    name: o.label,
    sortOrder: o.sortOrder,
    editing: false,
    editName: "",
  });

  const fetchBgCheck = useCallback(async () => {
    try {
      const [settingsResp, optionsResp] = await Promise.all([
        axiosPrivate.get("/api/background-check/settings"),
        axiosPrivate.get("/api/background-check/options"),
      ]);
      const s = settingsResp.data;
      setBgCheckEnabled(s.enabled);
      setBgCheckPrompt(s.prompt);
      setBgCheckOptions((optionsResp.data || []).map(toBgCheckOption));
    } catch (e) {
      enqueueSnackbar("Failed to load background check settings", { variant: "error" });
    }
  }, [axiosPrivate, enqueueSnackbar]);

  useEffect(() => {
    if (activeTab !== "general" || bgCheckLoadedOnce) return;
    setBgCheckLoadedOnce(true);
    fetchBgCheck();
  }, [activeTab, bgCheckLoadedOnce, fetchBgCheck]);

  const startEditBgCheck = (id) => setBgCheckOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: true, editName: o.name } : o));
  const cancelEditBgCheck = (id) => setBgCheckOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: false } : o));

  const saveEditBgCheck = async (id) => {
    const opt = bgCheckOptions.find((o) => o.id === id);
    if (!opt) return;
    setBgCheckOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: false, name: o.editName } : o));
    try {
      await axiosPrivate.put(`/api/background-check/options/${id}`, { label: opt.editName });
    } catch (e) {
      enqueueSnackbar("Failed to save option", { variant: "error" });
      await fetchBgCheck();
    }
  };

  const deleteBgCheckOption = async (id) => {
    setBgCheckOptions((prev) => prev.filter((o) => o.id !== id));
    try {
      await axiosPrivate.delete(`/api/background-check/options/${id}`);
    } catch (e) {
      enqueueSnackbar("Failed to delete option", { variant: "error" });
      await fetchBgCheck();
    }
  };

  const addBgCheckOption = async () => {
    if (!newBgCheckName.trim()) return;
    const label = newBgCheckName.trim();
    setNewBgCheckName("");
    setAddingBgCheck(false);
    try {
      const resp = await axiosPrivate.post("/api/background-check/options", { label });
      setBgCheckOptions((prev) => [...prev, toBgCheckOption(resp.data)]);
    } catch (e) {
      enqueueSnackbar("Failed to add option", { variant: "error" });
    }
  };

  // --- Required Immunizations ---
  const [immunizationsEnabled, setImmunizationsEnabled] = useState(true);
  const [immunizationsPrompt, setImmunizationsPrompt] = useState("Please select required immunizations");
  const [immunizationOptions, setImmunizationOptions] = useState([]);
  const [newImmunizationName, setNewImmunizationName] = useState("");
  const [addingImmunization, setAddingImmunization] = useState(false);
  const [immunizationsLoadedOnce, setImmunizationsLoadedOnce] = useState(false);
  const [immunizationsSaving, setImmunizationsSaving] = useState(false);

  const toImmunizationOption = (o) => ({
    id: o.id,
    name: o.label,
    sortOrder: o.sortOrder,
    editing: false,
    editName: "",
  });

  const fetchImmunizations = useCallback(async () => {
    try {
      const [settingsResp, optionsResp] = await Promise.all([
        axiosPrivate.get("/api/immunizations/settings"),
        axiosPrivate.get("/api/immunizations/options"),
      ]);
      const s = settingsResp.data;
      setImmunizationsEnabled(s.enabled);
      setImmunizationsPrompt(s.prompt);
      setImmunizationOptions((optionsResp.data || []).map(toImmunizationOption));
    } catch (e) {
      enqueueSnackbar("Failed to load immunization settings", { variant: "error" });
    }
  }, [axiosPrivate, enqueueSnackbar]);

  useEffect(() => {
    if (activeTab !== "general" || immunizationsLoadedOnce) return;
    setImmunizationsLoadedOnce(true);
    fetchImmunizations();
  }, [activeTab, immunizationsLoadedOnce, fetchImmunizations]);

  const startEditImmunization = (id) => setImmunizationOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: true, editName: o.name } : o));
  const cancelEditImmunization = (id) => setImmunizationOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: false } : o));

  const saveEditImmunization = async (id) => {
    const opt = immunizationOptions.find((o) => o.id === id);
    if (!opt) return;
    setImmunizationOptions((prev) => prev.map((o) => o.id === id ? { ...o, editing: false, name: o.editName } : o));
    try {
      await axiosPrivate.put(`/api/immunizations/options/${id}`, { label: opt.editName });
    } catch (e) {
      enqueueSnackbar("Failed to save option", { variant: "error" });
      await fetchImmunizations();
    }
  };

  const deleteImmunizationOption = async (id) => {
    setImmunizationOptions((prev) => prev.filter((o) => o.id !== id));
    try {
      await axiosPrivate.delete(`/api/immunizations/options/${id}`);
    } catch (e) {
      enqueueSnackbar("Failed to delete option", { variant: "error" });
      await fetchImmunizations();
    }
  };

  const addImmunizationOption = async () => {
    if (!newImmunizationName.trim()) return;
    const label = newImmunizationName.trim();
    setNewImmunizationName("");
    setAddingImmunization(false);
    try {
      const resp = await axiosPrivate.post("/api/immunizations/options", { label });
      setImmunizationOptions((prev) => [...prev, toImmunizationOption(resp.data)]);
    } catch (e) {
      enqueueSnackbar("Failed to add option", { variant: "error" });
    }
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onInsuranceDragEnd = ({ source, destination }) => {
    if (!destination) return;
    setInsuranceOptions((prev) => {
      const reordered = reorder(prev, source.index, destination.index);
      const updates = reordered.map((o, idx) => ({ id: o.id, sortOrder: idx }));
      axiosPrivate.put("/api/insurance/options/reorder", updates).catch(() =>
        enqueueSnackbar("Failed to save order", { variant: "error" })
      );
      return reordered;
    });
  };

  const onBgCheckDragEnd = ({ source, destination }) => {
    if (!destination) return;
    setBgCheckOptions((prev) => {
      const reordered = reorder(prev, source.index, destination.index);
      const updates = reordered.map((o, idx) => ({ id: o.id, sortOrder: idx }));
      axiosPrivate.put("/api/background-check/options/reorder", updates).catch(() =>
        enqueueSnackbar("Failed to save order", { variant: "error" })
      );
      return reordered;
    });
  };

  const onImmunizationDragEnd = ({ source, destination }) => {
    if (!destination) return;
    setImmunizationOptions((prev) => {
      const reordered = reorder(prev, source.index, destination.index);
      const updates = reordered.map((o, idx) => ({ id: o.id, sortOrder: idx }));
      axiosPrivate.put("/api/immunizations/options/reorder", updates).catch(() =>
        enqueueSnackbar("Failed to save order", { variant: "error" })
      );
      return reordered;
    });
  };

  // ── Categories tab ──────────────────────────────────────────────────────────

  const [categoriesLoadedOnce, setCategoriesLoadedOnce] = useState(false);

  // Event Categories  (/api/basic/CMS_Category)
  const [eventCategories, setEventCategories] = useState([]);
  const [eventCategoriesLoading, setEventCategoriesLoading] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);

  // Project Groups — shared list for Partnership + Accounts Receivable  (/api/events/projectgroups)
  const [projectGroups, setProjectGroups] = useState([]);
  const [projectGroupsLoading, setProjectGroupsLoading] = useState(false);
  const [newProjectGroupName, setNewProjectGroupName] = useState("");
  const [newProjectGroupAccount, setNewProjectGroupAccount] = useState("");
  const [addingProjectGroup, setAddingProjectGroup] = useState(false);

  const toEventCategory = (c) => ({ id: c.CategoryID, name: c.CategoryDisplayName, editing: false, editName: "" });
  const toProjectGroup  = (g) => ({
    id: g.ItemID,
    name: g.Name,
    accountingNumber: g.AccountingNumber ?? g.ProjectCode ?? "",
    editing: false,
    editName: "",
    editAccountingNumber: "",
  });

  const fetchCategoriesTab = useCallback(async () => {
    setEventCategoriesLoading(true);
    setProjectGroupsLoading(true);
    try {
      const [catResp, pgResp] = await Promise.all([
        axiosPrivate.get("/api/basic/CMS_Category"),
        axiosPrivate.get("/api/events/projectgroups"),
      ]);
      setEventCategories((catResp.data || []).map((c) => ({ id: c.CategoryID, name: c.CategoryDisplayName, editing: false, editName: "" })));
      setProjectGroups((pgResp.data || []).map(toProjectGroup));
    } catch (e) {
      enqueueSnackbar("Failed to load categories data", { variant: "error" });
    } finally {
      setEventCategoriesLoading(false);
      setProjectGroupsLoading(false);
    }
  }, [axiosPrivate, enqueueSnackbar]);

  useEffect(() => {
    if (activeTab !== "categories" || categoriesLoadedOnce) return;
    setCategoriesLoadedOnce(true);
    fetchCategoriesTab();
  }, [activeTab, categoriesLoadedOnce, fetchCategoriesTab]);

  // — Event Category CRUD —
  const startEditCategory  = (id) => setEventCategories((p) => p.map((c) => c.id === id ? { ...c, editing: true,  editName: c.name } : c));
  const cancelEditCategory = (id) => setEventCategories((p) => p.map((c) => c.id === id ? { ...c, editing: false } : c));

  const saveEditCategory = async (id) => {
    const cat = eventCategories.find((c) => c.id === id);
    if (!cat) return;
    setEventCategories((p) => p.map((c) => c.id === id ? { ...c, editing: false, name: c.editName } : c));
    try {
      await axiosPrivate.put(`/api/basic/CMS_Category/${id}`, { CategoryDisplayName: cat.editName });
      enqueueSnackbar("Category updated", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Failed to save category", { variant: "error" });
      await fetchCategoriesTab();
    }
  };

  const deleteCategory = async (id) => {
    setEventCategories((p) => p.filter((c) => c.id !== id));
    try {
      await axiosPrivate.delete(`/api/basic/CMS_Category/${id}`);
      enqueueSnackbar("Category deleted", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Failed to delete category", { variant: "error" });
      await fetchCategoriesTab();
    }
  };

  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    const name = newCategoryName.trim();
    setNewCategoryName("");
    setAddingCategory(false);
    try {
      const resp = await axiosPrivate.post("/api/basic/CMS_Category", { CategoryDisplayName: name });
      setEventCategories((p) => [...p, toEventCategory(resp.data)]);
      enqueueSnackbar("Category added", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Failed to add category", { variant: "error" });
    }
  };

  // — Project Group CRUD —
  const startEditProjectGroup  = (id) => setProjectGroups((p) => p.map((g) => g.id === id ? { ...g, editing: true, editName: g.name, editAccountingNumber: g.accountingNumber || "" } : g));
  const cancelEditProjectGroup = (id) => setProjectGroups((p) => p.map((g) => g.id === id ? { ...g, editing: false } : g));

  const saveEditProjectGroup = async (id) => {
    const grp = projectGroups.find((g) => g.id === id);
    if (!grp) return;
    const nextName = grp.editName.trim();
    const nextAccountingNumber = (grp.editAccountingNumber || "").trim();
    setProjectGroups((p) => p.map((g) => g.id === id
      ? { ...g, editing: false, name: nextName, accountingNumber: nextAccountingNumber }
      : g));
    try {
      await axiosPrivate.put(`/api/events/projectgroups/${id}`, {
        Name: nextName,
        AccountingNumber: nextAccountingNumber,
      });
      enqueueSnackbar("Group updated", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Failed to save group", { variant: "error" });
      await fetchCategoriesTab();
    }
  };

  const deleteProjectGroup = async (id) => {
    setProjectGroups((p) => p.filter((g) => g.id !== id));
    try {
      await axiosPrivate.delete(`/api/events/projectgroups/${id}`);
      enqueueSnackbar("Group deleted", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Failed to delete group", { variant: "error" });
      await fetchCategoriesTab();
    }
  };

  const addProjectGroup = async () => {
    if (!newProjectGroupName.trim()) return;
    const name = newProjectGroupName.trim();
    const accountingNumber = (newProjectGroupAccount || "").trim();
    setNewProjectGroupName("");
    setNewProjectGroupAccount("");
    setAddingProjectGroup(false);
    try {
      const resp = await axiosPrivate.post("/api/events/projectgroups", {
        Name: name,
        AccountingNumber: accountingNumber,
      });
      setProjectGroups((p) => [...p, toProjectGroup(resp.data)]);
      enqueueSnackbar("Group added", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Failed to add group", { variant: "error" });
    }
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#0074c8" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#0074c8" },
  };

  const [hasLoadedTermsOnce, setHasLoadedTermsOnce] = useState(false);
  const [isLoading, setIsLoading] = useState(activeTab === "terms");
  const [isSaving, setIsSaving] = useState(false);

  const [policyHeading, setPolicyHeading] = useState("");
  const [policyContent, setPolicyContent] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null); // ISO string | null

  const formatApiError = (e) => {
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (data?.error) return data.error;
    if (typeof data === "string") return `${status ? `${status}: ` : ""}${data}`.trim();
    if (data) return `${status ? `${status}: ` : ""}${JSON.stringify(data)}`.trim();
    return e?.message || "Unknown error";
  };

  const formattedLastUpdated = useMemo(() => {
    if (!lastUpdated) return "—";
    const dt = DateTime.fromISO(lastUpdated);
    if (!dt.isValid) return lastUpdated;
    return dt.toLocaleString(DateTime.DATETIME_MED);
  }, [lastUpdated]);

  const fetchTerms = useCallback(async ({ silent = false } = {}) => {
    setIsLoading(true);
    try {
      const resp = await axiosPrivate.get("/api/settings/terms");
      setPolicyHeading(resp?.data?.heading ?? "");
      setPolicyContent(resp?.data?.content ?? "");
      setLastUpdated(resp?.data?.lastUpdated ?? null);
      if (!silent) enqueueSnackbar("Settings loaded", { variant: "success" });
    } catch (e) {
      if (!silent) enqueueSnackbar(formatApiError(e) || "Failed to load settings", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, enqueueSnackbar]);

  useEffect(() => {
    if (activeTab !== "terms") return;
    if (hasLoadedTermsOnce) return;
    setHasLoadedTermsOnce(true);
    fetchTerms({ silent: true });
  }, [activeTab, hasLoadedTermsOnce, fetchTerms]);

  const handleTabChange = (_event, nextTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", nextTab);
      return next;
    }, { replace: true });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axiosPrivate.put("/api/settings/terms", {
        heading: policyHeading,
        content: policyContent,
      });
      enqueueSnackbar("Policy saved", { variant: "success" });
      await fetchTerms({ silent: true }); // refresh Last Updated + persisted values
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || "Failed to save policy", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Settings</h1>
              <p>Configure application settings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
      {/* Sticky Tab Bar */}
      <Box
        sx={{
          position: "sticky",
          top: 40,
          zIndex: 100,
          backgroundColor: "white",
          borderBottom: 1,
          borderColor: "divider",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <Box sx={{ px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ sx: { height: 3, borderRadius: 2, backgroundColor: "#0074c8" } }}
          sx={{
            minHeight: 44,
            "& .MuiTabs-flexContainer": { gap: 1 },
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: "proxima-nova, sans-serif",
              fontWeight: 600,
              fontSize: { xs: 14, sm: 16 },
              minHeight: 44,
              px: 2,
              borderRadius: 1,
              whiteSpace: "nowrap",
              color: "text.primary",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
              "&.Mui-selected": { color: "#0074c8" },
            },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} wrapped={false} />
          ))}
        </Tabs>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>

      <TabPanel activeTab={activeTab} tab="general">
        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          {/* Agreements header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ textTransform: "none", fontWeight: 600, color: "#21252C" }}>Agreements</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={agreementsEnabled}
                  onChange={(e) => setAgreementsEnabled(e.target.checked)}
                  sx={switchSx}
                />
              }
              label="Enabled"
              labelPlacement="start"
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          {agreementsLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading agreements...</Typography>
            </Box>
          ) : (
            <>
              {agreements.map((agreement, index) => (
                <Box key={agreement.id}>
                  {/* Section header with name + enabled toggle */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: "none", color: "#21252C" }}>
                      {agreement.name}
                    </Typography>
                    <FormControlLabel
                      control={<Switch checked={agreement.enabled ?? true} onChange={(e) => handleAgreementChange(agreement.id, "enabled", e.target.checked)} size="small" sx={switchSx} />}
                      label="Enabled"
                      labelPlacement="start"
                    />
                  </Box>

                  {/* Row 1: File upload (left) | Registration Field Label (right) */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={5}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: "#21252C", fontSize: 12, fontWeight: 400 }}>
                        {agreement.name}
                      </Typography>
                      <input
                        type="file"
                        accept="application/pdf"
                        style={{ display: "none" }}
                        ref={(el) => { fileInputRefs.current[agreement.id] = el; }}
                        onChange={(e) => { handleReplacePdf(agreement.id, e.target.files?.[0]); e.target.value = ""; }}
                      />
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "#fafafa",
                          minHeight: 44,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
                          <Box component="span" sx={{ color: "#d32f2f", flexShrink: 0, fontSize: 18, lineHeight: 1 }}>
                            &#x1F4C4;
                          </Box>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ color: "text.secondary" }}
                            title={agreement.pdfFileName || `${agreement.name.replace(/ /g, "-")}.pdf`}
                          >
                            {truncateFileName(agreement.pdfFileName || `${agreement.name.replace(/ /g, "-")}.pdf`)}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => fileInputRefs.current[agreement.id]?.click()}
                          sx={{ ml: 1, whiteSpace: "nowrap", textTransform: "none", flexShrink: 0 }}
                        >
                          Replace
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: "#21252C", fontSize: 12, fontWeight: 400 }}>
                        Registration Field Label
                      </Typography>
                      <TextField
                        value={agreement.registrationFieldLabel || ""}
                        onChange={(e) => handleAgreementChange(agreement.id, "registrationFieldLabel", e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>

                  {/* Row 2: Instructions (left) | Link Text (right) */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: "#21252C", fontSize: 12, fontWeight: 400 }}>
                        Instructions
                      </Typography>
                      <TextField
                        value={agreement.instructions || ""}
                        onChange={(e) => handleAgreementChange(agreement.id, "instructions", e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: "#21252C", fontSize: 12, fontWeight: 400 }}>
                        Link Text
                      </Typography>
                      <TextField
                        value={agreement.linkText || ""}
                        onChange={(e) => handleAgreementChange(agreement.id, "linkText", e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>

                  {index < agreements.length - 1 && <Divider sx={{ my: 3 }} />}
                </Box>
              ))}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  mt: agreements.length > 0 ? 3 : 0,
                  // Mobile only: center the single Save button.
                  "@media (max-width:767px)": {
                    justifyContent: "center",
                  },
                }}
              >
                <Button variant="contained" disabled={agreementsSaving} onClick={handleSaveAgreements}>
                  {agreementsSaving ? "Saving..." : "Save Agreements"}
                </Button>
              </Box>
            </>
          )}
        </Paper>

        {/* ── Supplemental Insurance / Background Check / Required Immunizations ── */}
        <Paper elevation={0} sx={{ p: 3, mt: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>

          {/* ── Supplemental Insurance ── */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: "none", color: "#21252C" }}>Supplemental Insurance</Typography>
            <FormControlLabel
              control={<Switch checked={suppInsuranceEnabled} onChange={(e) => setSuppInsuranceEnabled(e.target.checked)} sx={switchSx} />}
              label="Enabled"
              labelPlacement="start"
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ mb: 0.5, color: "#21252C", fontSize: 12, fontWeight: 400 }}>Registration Prompt</Typography>
              <TextField
                value={suppInsurancePrompt}
                onChange={(e) => setSuppInsurancePrompt(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
              />
              <FormControlLabel
                control={<Checkbox checked={allowSelfProvided} onChange={(e) => setAllowSelfProvided(e.target.checked)} size="small" sx={{ color: "#0074c8", "&.Mui-checked": { color: "#0074c8" } }} />}
                label={<Typography variant="body2">Allow Self-Provided Coverage</Typography>}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography variant="body2" sx={{ mb: 1, color: "#21252C", fontSize: 12, fontWeight: 400 }}>Insurance Options</Typography>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <DragDropContext onDragEnd={onInsuranceDragEnd}>
                  <Droppable droppableId="insurance-options">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {insuranceOptions.map((opt, idx) => (
                          <Draggable key={String(opt.id)} draggableId={String(opt.id)} index={idx}>
                            {(dragProvided) => (
                              <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                                <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderBottom: idx < insuranceOptions.length - 1 ? "1px solid" : "none", borderColor: "divider", backgroundColor: "#fff" }}>
                                  {opt.editing ? (
                                    <>
                                      <TextField value={opt.editName} onChange={(e) => setInsuranceOptions((prev) => prev.map((o) => o.id === opt.id ? { ...o, editName: e.target.value } : o))} size="small" sx={{ flex: 2 }} />
                                      <TextField value={opt.editPrice} onChange={(e) => setInsuranceOptions((prev) => prev.map((o) => o.id === opt.id ? { ...o, editPrice: e.target.value } : o))} size="small" placeholder="$0.00 / person / day" sx={{ flex: 1.5 }} />
                                      <Button size="small" variant="contained" onClick={() => saveEditInsurance(opt.id)} sx={{ textTransform: "none", minWidth: 0, px: 1 }}>Save</Button>
                                      <Button size="small" onClick={() => cancelEditInsurance(opt.id)} sx={{ textTransform: "none", minWidth: 0, px: 1 }}>Cancel</Button>
                                    </>
                                  ) : (
                                    <>
                                      <Box {...dragProvided.dragHandleProps} sx={{ display: "flex", alignItems: "center", color: "text.disabled", cursor: "grab", flexShrink: 0 }}>
                                        <DragIndicatorIcon fontSize="small" />
                                      </Box>
                                      <Typography variant="body2" sx={{ flex: 2 }}>{opt.name}</Typography>
                                      <Typography variant="body2" sx={{ flex: 1.5, color: "text.secondary" }}>{opt.price}</Typography>
                                      <IconButton size="small" onClick={() => startEditInsurance(opt.id)}><EditOutlinedIcon fontSize="small" /></IconButton>
                                      <IconButton size="small" onClick={() => deleteInsuranceOption(opt.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                    </>
                                  )}
                                </Box>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                {addingInsurance && (
                  <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderTop: "1px solid", borderColor: "divider" }}>
                    <TextField value={newInsuranceName} onChange={(e) => setNewInsuranceName(e.target.value)} size="small" placeholder="Option name" sx={{ flex: 2 }} />
                    <TextField value={newInsurancePrice} onChange={(e) => setNewInsurancePrice(e.target.value)} size="small" placeholder="$0.00 / person / day" sx={{ flex: 1.5 }} />
                    <Button size="small" variant="contained" onClick={addInsuranceOption} sx={{ textTransform: "none", minWidth: 0, px: 1 }}>Add</Button>
                    <Button size="small" onClick={() => { setAddingInsurance(false); setNewInsuranceName(""); setNewInsurancePrice(""); }} sx={{ textTransform: "none", minWidth: 0, px: 1 }}>Cancel</Button>
                  </Box>
                )}
              </Box>
              {!addingInsurance && (
                <Button size="small" variant="outlined" onClick={() => setAddingInsurance(true)} sx={{ mt: 1, textTransform: "none", color: "#0074c8", borderColor: "#0074c8", fontWeight: 500, fontSize: 14, px: 2, py: 0.75, "&:hover": { borderColor: "#005fa3", color: "#005fa3", backgroundColor: "rgba(0,116,200,0.04)" } }}>
                  + Add Option
                </Button>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ── Background Check ── */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: "none", color: "#21252C" }}>Background Check</Typography>
            <FormControlLabel
              control={<Switch checked={bgCheckEnabled} onChange={(e) => setBgCheckEnabled(e.target.checked)} sx={switchSx} />}
              label="Enabled"
              labelPlacement="start"
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ mb: 0.5, color: "#21252C", fontSize: 12, fontWeight: 400 }}>Registration Prompt</Typography>
              <TextField
                value={bgCheckPrompt}
                onChange={(e) => setBgCheckPrompt(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography variant="body2" sx={{ mb: 1, color: "#21252C", fontSize: 12, fontWeight: 400 }}>Background Check Options</Typography>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <DragDropContext onDragEnd={onBgCheckDragEnd}>
                  <Droppable droppableId="bgcheck-options">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {bgCheckOptions.map((opt, idx) => (
                          <Draggable key={String(opt.id)} draggableId={String(opt.id)} index={idx}>
                            {(dragProvided) => (
                              <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                                <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderBottom: idx < bgCheckOptions.length - 1 ? "1px solid" : "none", borderColor: "divider", backgroundColor: "#fff" }}>
                                  {opt.editing ? (
                                    <>
                                      <TextField value={opt.editName} onChange={(e) => setBgCheckOptions((prev) => prev.map((o) => o.id === opt.id ? { ...o, editName: e.target.value } : o))} size="small" fullWidth />
                                      <Button size="small" variant="contained" onClick={() => saveEditBgCheck(opt.id)} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Save</Button>
                                      <Button size="small" onClick={() => cancelEditBgCheck(opt.id)} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Cancel</Button>
                                    </>
                                  ) : (
                                    <>
                                      <Box {...dragProvided.dragHandleProps} sx={{ display: "flex", alignItems: "center", color: "text.disabled", cursor: "grab", flexShrink: 0 }}>
                                        <DragIndicatorIcon fontSize="small" />
                                      </Box>
                                      <Typography variant="body2" sx={{ flex: 1 }}>{opt.name}</Typography>
                                      <IconButton size="small" onClick={() => startEditBgCheck(opt.id)}><EditOutlinedIcon fontSize="small" /></IconButton>
                                      <IconButton size="small" onClick={() => deleteBgCheckOption(opt.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                    </>
                                  )}
                                </Box>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                {addingBgCheck && (
                  <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderTop: "1px solid", borderColor: "divider" }}>
                    <TextField value={newBgCheckName} onChange={(e) => setNewBgCheckName(e.target.value)} size="small" placeholder="Option name" fullWidth />
                    <Button size="small" variant="contained" onClick={addBgCheckOption} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Add</Button>
                    <Button size="small" onClick={() => { setAddingBgCheck(false); setNewBgCheckName(""); }} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Cancel</Button>
                  </Box>
                )}
              </Box>
              {!addingBgCheck && (
                <Button size="small" variant="outlined" onClick={() => setAddingBgCheck(true)} sx={{ mt: 1, textTransform: "none", color: "#0074c8", borderColor: "#0074c8", fontWeight: 500, fontSize: 14, px: 2, py: 0.75, "&:hover": { borderColor: "#005fa3", color: "#005fa3", backgroundColor: "rgba(0,116,200,0.04)" } }}>
                  + Add Option
                </Button>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ── Required Immunizations ── */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: "none", color: "#21252C" }}>Required Immunizations</Typography>
            <FormControlLabel
              control={<Switch checked={immunizationsEnabled} onChange={(e) => setImmunizationsEnabled(e.target.checked)} sx={switchSx} />}
              label="Enabled"
              labelPlacement="start"
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ mb: 0.5, color: "#21252C", fontSize: 12, fontWeight: 400 }}>Immunizations Prompt</Typography>
              <TextField
                value={immunizationsPrompt}
                onChange={(e) => setImmunizationsPrompt(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography variant="body2" sx={{ mb: 1, color: "#21252C", fontSize: 12, fontWeight: 400 }}>Immunizations Options</Typography>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <DragDropContext onDragEnd={onImmunizationDragEnd}>
                  <Droppable droppableId="immunization-options">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {immunizationOptions.map((opt, idx) => (
                          <Draggable key={String(opt.id)} draggableId={String(opt.id)} index={idx}>
                            {(dragProvided) => (
                              <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                                <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderBottom: idx < immunizationOptions.length - 1 ? "1px solid" : "none", borderColor: "divider", backgroundColor: "#fff" }}>
                                  {opt.editing ? (
                                    <>
                                      <TextField value={opt.editName} onChange={(e) => setImmunizationOptions((prev) => prev.map((o) => o.id === opt.id ? { ...o, editName: e.target.value } : o))} size="small" fullWidth />
                                      <Button size="small" variant="contained" onClick={() => saveEditImmunization(opt.id)} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Save</Button>
                                      <Button size="small" onClick={() => cancelEditImmunization(opt.id)} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Cancel</Button>
                                    </>
                                  ) : (
                                    <>
                                      <Box {...dragProvided.dragHandleProps} sx={{ display: "flex", alignItems: "center", color: "text.disabled", cursor: "grab", flexShrink: 0 }}>
                                        <DragIndicatorIcon fontSize="small" />
                                      </Box>
                                      <Typography variant="body2" sx={{ flex: 1 }}>{opt.name}</Typography>
                                      <IconButton size="small" onClick={() => startEditImmunization(opt.id)}><EditOutlinedIcon fontSize="small" /></IconButton>
                                      <IconButton size="small" onClick={() => deleteImmunizationOption(opt.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                    </>
                                  )}
                                </Box>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                {addingImmunization && (
                  <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderTop: "1px solid", borderColor: "divider" }}>
                    <TextField value={newImmunizationName} onChange={(e) => setNewImmunizationName(e.target.value)} size="small" placeholder="Option name" fullWidth />
                    <Button size="small" variant="contained" onClick={addImmunizationOption} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Add</Button>
                    <Button size="small" onClick={() => { setAddingImmunization(false); setNewImmunizationName(""); }} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Cancel</Button>
                  </Box>
                )}
              </Box>
              {!addingImmunization && (
                <Button size="small" variant="outlined" onClick={() => setAddingImmunization(true)} sx={{ mt: 1, textTransform: "none", color: "#0074c8", borderColor: "#0074c8", fontWeight: 500, fontSize: 14, px: 2, py: 0.75, "&:hover": { borderColor: "#005fa3", color: "#005fa3", backgroundColor: "rgba(0,116,200,0.04)" } }}>
                  + Add Option
                </Button>
              )}
            </Grid>
          </Grid>

        </Paper>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-start",
            // Mobile only: center the single Save button.
            "@media (max-width:767px)": {
              justifyContent: "center",
            },
          }}
        >
          <Button variant="contained" disabled={insuranceSaving || bgCheckSaving || immunizationsSaving} onClick={handleSaveAllSettings}>
            {(insuranceSaving || bgCheckSaving || immunizationsSaving) ? "Saving..." : "Save Settings"}
          </Button>
        </Box>

      </TabPanel>

      <TabPanel activeTab={activeTab} tab="terms">
        <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}>
              <CircularProgress size={22} />
              <Typography>Loading Terms &amp; Conditions…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 2,
                  // Mobile only: stack the title and "Last Updated" vertically.
                  "@media (max-width:767px)": {
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.5,
                  },
                }}
              >
                <Typography variant="h6" sx={{ textTransform: "none", fontWeight: 600, color: "#21252C" }}>Terms &amp; Conditions Policy</Typography>
                <Typography variant="body2" sx={{ color: "#21252C", fontSize: 12, fontWeight: 400 }}>
                  Last Updated: {formattedLastUpdated}
                </Typography>
              </Box>

              <TextField
                label="Policy Heading"
                value={policyHeading}
                onChange={(e) => setPolicyHeading(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 200 }}
                helperText="Heading/title for the Terms & Conditions policy"
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#21252C" }}>
                  Policy Content
                </Typography>
                <JoditEditor
                  ref={editor}
                  config={config}
                  value={policyContent}
                  onChange={(content) => setPolicyContent(content)}
                />
                <Typography variant="caption" sx={{ color: "#21252C", display: "block", mt: 0.5, fontSize: 12, fontWeight: 400 }}>
                  Full Terms &amp; Conditions body copy
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 1,
                  // Mobile only: both buttons share one full-width row.
                  "@media (max-width:767px)": {
                    "& > button": { flex: "1 1 0" },
                  },
                }}
              >
                <Button variant="contained" disabled={isSaving} onClick={handleSave}>
                  {isSaving ? "Saving..." : "Save"}
                </Button>
                <Button variant="outlined" disabled={isSaving} onClick={() => fetchTerms({ silent: true })}>
                  Reset
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* ── Categories Tab ── */}
      <TabPanel activeTab={activeTab} tab="categories">

        {/* Event Categories */}
        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Typography variant="h6" sx={{ textTransform: "none", fontWeight: 600, color: "#21252C", mb: 1 }}>
            Event Categories
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Shared list used for the <strong>Category</strong> select field on events. Existing IDs and names are preserved.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {eventCategoriesLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading categories…</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                {eventCategories.map((cat, idx) => (
                  <Box
                    key={cat.id}
                    sx={{
                      px: 1.5, py: 0.75,
                      display: "flex", alignItems: "center", gap: 1,
                      borderBottom: idx < eventCategories.length - 1 ? "1px solid" : "none",
                      borderColor: "divider",
                      backgroundColor: "#fff",
                    }}
                  >
                    {cat.editing ? (
                      <>
                        <TextField
                          value={cat.editName}
                          onChange={(e) => setEventCategories((p) => p.map((c) => c.id === cat.id ? { ...c, editName: e.target.value } : c))}
                          size="small"
                          fullWidth
                        />
                        <Button size="small" variant="contained" onClick={() => saveEditCategory(cat.id)} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Save</Button>
                        <Button size="small" onClick={() => cancelEditCategory(cat.id)} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ flex: 1 }}>{cat.name}</Typography>
                        <Typography variant="caption" sx={{ color: "text.disabled", mr: 0.5, flexShrink: 0 }}>ID: {cat.id}</Typography>
                        <IconButton size="small" onClick={() => startEditCategory(cat.id)}><EditOutlinedIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => deleteCategory(cat.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </>
                    )}
                  </Box>
                ))}
                {addingCategory && (
                  <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderTop: eventCategories.length > 0 ? "1px solid" : "none", borderColor: "divider" }}>
                    <TextField value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} size="small" placeholder="Category name" fullWidth />
                    <Button size="small" variant="contained" onClick={addCategory} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Add</Button>
                    <Button size="small" onClick={() => { setAddingCategory(false); setNewCategoryName(""); }} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Cancel</Button>
                  </Box>
                )}
              </Box>
              {!addingCategory && (
                <Button
                  size="small" variant="outlined"
                  onClick={() => setAddingCategory(true)}
                  sx={{ mt: 1, textTransform: "none", color: "#0074c8", borderColor: "#0074c8", fontWeight: 500, fontSize: 14, px: 2, py: 0.75, "&:hover": { borderColor: "#005fa3", color: "#005fa3", backgroundColor: "rgba(0,116,200,0.04)" } }}
                >
                  + Add Category
                </Button>
              )}
            </>
          )}
        </Paper>

        {/* Partnership / Accounts Receivable */}
        <Paper elevation={0} sx={{ p: 3, mt: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Typography variant="h6" sx={{ textTransform: "none", fontWeight: 600, color: "#21252C", mb: 1 }}>
            Partnership &amp; Accounts Receivable
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Shared list used for both the <strong>Partnership</strong> and <strong>Accounts Receivable</strong> select fields on events. Each item has a <strong>Name</strong>, auto-generated <strong>ID</strong>, and an <strong>Accounting Number</strong> (e.g. <code>4-49915-J1-49022</code>).
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {projectGroupsLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading groups…</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                {projectGroups.map((grp, idx) => (
                  <Box
                    key={grp.id}
                    sx={{
                      px: 1.5, py: 0.75,
                      display: "flex", alignItems: "center", gap: 1,
                      borderBottom: idx < projectGroups.length - 1 ? "1px solid" : "none",
                      borderColor: "divider",
                      backgroundColor: "#fff",
                    }}
                  >
                    {grp.editing ? (
                      <>
                        <TextField
                          value={grp.editName}
                          onChange={(e) => setProjectGroups((p) => p.map((g) => g.id === grp.id ? { ...g, editName: e.target.value } : g))}
                          size="small"
                          placeholder="Name"
                          sx={{ flex: 2 }}
                        />
                        <TextField
                          value={grp.editAccountingNumber}
                          onChange={(e) => setProjectGroups((p) => p.map((g) => g.id === grp.id ? { ...g, editAccountingNumber: e.target.value } : g))}
                          size="small"
                          placeholder="Accounting Number"
                          sx={{ flex: 2 }}
                        />
                        <Button size="small" variant="contained" onClick={() => saveEditProjectGroup(grp.id)} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Save</Button>
                        <Button size="small" onClick={() => cancelEditProjectGroup(grp.id)} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ flex: "1 1 40%", minWidth: "90px" }}>{grp.name}</Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: grp.accountingNumber ? "text.secondary" : "text.disabled", mr: 1, minWidth: 0, flexShrink: 1, overflowWrap: "anywhere", fontFamily: "monospace" }}
                        >
                          {grp.accountingNumber ? `Acct: ${grp.accountingNumber}` : "Acct: —"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.disabled", mr: 0.5, flexShrink: 0 }}>ID: {grp.id}</Typography>
                        <Box sx={{ display: "flex", flexShrink: 0 }}>
                          <IconButton size="small" onClick={() => startEditProjectGroup(grp.id)}><EditOutlinedIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => deleteProjectGroup(grp.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Box>
                      </>
                    )}
                  </Box>
                ))}
                {addingProjectGroup && (
                  <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderTop: projectGroups.length > 0 ? "1px solid" : "none", borderColor: "divider" }}>
                    <TextField value={newProjectGroupName} onChange={(e) => setNewProjectGroupName(e.target.value)} size="small" placeholder="Name" sx={{ flex: 2 }} />
                    <TextField value={newProjectGroupAccount} onChange={(e) => setNewProjectGroupAccount(e.target.value)} size="small" placeholder="Accounting Number (e.g. 4-49915-J1-49022)" sx={{ flex: 2 }} />
                    <Button size="small" variant="contained" onClick={addProjectGroup} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Add</Button>
                    <Button size="small" onClick={() => { setAddingProjectGroup(false); setNewProjectGroupName(""); setNewProjectGroupAccount(""); }} sx={{ textTransform: "none", minWidth: 0, px: 1, flexShrink: 0 }}>Cancel</Button>
                  </Box>
                )}
              </Box>
              {!addingProjectGroup && (
                <Button
                  size="small" variant="outlined"
                  onClick={() => setAddingProjectGroup(true)}
                  sx={{ mt: 1, textTransform: "none", color: "#0074c8", borderColor: "#0074c8", fontWeight: 500, fontSize: 14, px: 2, py: 0.75, "&:hover": { borderColor: "#005fa3", color: "#005fa3", backgroundColor: "rgba(0,116,200,0.04)" } }}
                >
                  + Add Group
                </Button>
              )}
            </>
          )}
        </Paper>

      </TabPanel>
    </Box>
      </div>
    </main>
  );
};

export default Settings;
